import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import Icon, { ICON_SIZES } from '../../../src/components/Icon';
import { useNotificationStore } from '../../../src/stores/notificationStore';
import { useNetworkStore } from '../../../src/stores/networkStore';
import { AppNotification, settingsApi } from '../../../src/api/client';

// Map notification type to icon name
const TYPE_ICON_MAP: Record<string, string> = {
  welcome: 'gift-outline',
  export_completed: 'download-outline',
  security_new_device: 'shield-outline',
  security_passkey_added: 'shield-outline',
  team_invited: 'people-outline',
  team_removed: 'people-outline',
  team_ownership_received: 'people-outline',
  team_member_joined: 'people-outline',
  goal_deadline_approaching: 'flag-outline',
  activity_streak_milestone: 'flame-outline',
  goal_activities_completed: 'checkmark-circle-outline',
  app_update_available: 'arrow-up-circle-outline',
  maintenance_notice: 'construct-outline',
  feature_announcement: 'sparkles-outline',
};

function getIconForType(type: string): string {
  return TYPE_ICON_MAP[type] ?? 'notifications-outline';
}

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  header: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerTitle: {
    fontSize: 22,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
  },
  markAllButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: theme.colors.bgTertiary,
  },
  markAllText: {
    fontSize: 13,
    fontWeight: '600' as const,
    color: theme.colors.primary,
  },
  listContent: { paddingHorizontal: 16, paddingBottom: 24 },
  card: {
    flexDirection: 'row' as const,
    backgroundColor: theme.colors.background,
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    ...theme.shadows.sm,
    gap: 12,
  },
  cardUnread: {
    borderLeftWidth: 3,
    borderLeftColor: theme.colors.primary,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.bgTertiary,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  content: { flex: 1 },
  titleRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'flex-start' as const,
    marginBottom: 4,
  },
  title: {
    fontSize: 15,
    fontWeight: '600' as const,
    color: theme.colors.textPrimary,
    flex: 1,
    marginRight: 8,
  },
  titleRead: {
    fontWeight: '400' as const,
    color: theme.colors.textSecondary,
  },
  time: {
    fontSize: 12,
    color: theme.colors.textDisabled,
    fontWeight: '500' as const,
  },
  body: {
    fontSize: 14,
    color: theme.colors.textMuted,
    lineHeight: 20,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.colors.primary,
    position: 'absolute' as const,
    top: 0,
    right: 0,
  },
  disabledBanner: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.colors.bgTertiary,
    marginHorizontal: 16,
    marginTop: 8,
    marginBottom: 4,
    padding: 12,
    borderRadius: 10,
    gap: 10,
  },
  disabledBannerText: {
    flex: 1,
    fontSize: 13,
    color: theme.colors.textSecondary,
    lineHeight: 18,
  },
  disabledBannerLink: {
    fontSize: 13,
    fontWeight: '600' as const,
    color: theme.colors.primary,
  },
  emptyContainer: {
    flexGrow: 1,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    paddingTop: 80,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: theme.colors.textSecondary,
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 15,
    color: theme.colors.textDisabled,
    textAlign: 'center' as const,
    lineHeight: 22,
    paddingHorizontal: 40,
  },
  ctaButton: {
    marginTop: 20,
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  ctaText: {
    color: theme.colors.background,
    fontSize: 15,
    fontWeight: '600' as const,
  },
});

export default function NotificationsScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  const [notificationsDisabled, setNotificationsDisabled] = useState(false);
  const connectivityState = useNetworkStore((s) => s.connectivityState);

  const {
    notifications,
    isLoading,
    fetchNotifications,
    markRead,
  } = useNotificationStore();

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      (async () => {
        try {
          const res = await settingsApi.get();
          if (!cancelled) setNotificationsDisabled(res.data.notifications_enabled === false);
        } catch {
          // Silently fail — don't block the screen
        }
      })();
      return () => { cancelled = true; };
    }, [])
  );

  const handleRefresh = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handlePress = useCallback(
    (notification: AppNotification) => {
      if (!notification.is_read) {
        markRead([notification.id]);
      }
      if (notification.action_url) {
        router.push(notification.action_url as any);
      }
    },
    [markRead, router],
  );

  const renderItem = useCallback(
    ({ item }: { item: AppNotification }) => (
      <TouchableOpacity
        style={[styles.card, !item.is_read && styles.cardUnread]}
        onPress={() => handlePress(item)}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`${item.title}. ${item.body}`}
        testID={`notification.item-${item.id}`}
      >
        <View style={styles.iconContainer}>
          <Icon
            name={getIconForType(item.type) as any}
            size={ICON_SIZES.menu}
            color={!item.is_read ? colors.primary : colors.textMuted}
          />
        </View>
        <View style={styles.content}>
          <View style={styles.titleRow}>
            <Text
              style={[styles.title, item.is_read && styles.titleRead]}
              numberOfLines={1}
            >
              {item.title}
            </Text>
            <Text style={styles.time}>{formatRelativeTime(item.created_at)}</Text>
          </View>
          <Text style={styles.body} numberOfLines={2}>
            {item.body}
          </Text>
        </View>
      </TouchableOpacity>
    ),
    [styles, colors, handlePress],
  );

  const keyExtractor = useCallback((item: AppNotification) => item.id, []);

  return (
    <View style={styles.container}>
      {notificationsDisabled && (
        <View style={styles.disabledBanner}>
          <Icon name="notifications-off-outline" size={ICON_SIZES.menu} color={colors.textDisabled} />
          <Text style={styles.disabledBannerText}>
            Notifications are disabled.{' '}
            <Text
              style={styles.disabledBannerLink}
              onPress={() => router.push('/(tabs)/settings/notification-preferences' as any)}
              accessibilityRole="link"
            >
              Enable in Settings &gt; Preferences &gt; Notifications
            </Text>
          </Text>
        </View>
      )}

      <FlatList
        data={notifications}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        contentContainerStyle={[
          styles.listContent,
          notifications.length === 0 && { flexGrow: 1 },
        ]}
        refreshControl={
          <RefreshControl
            refreshing={isLoading}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
        ListEmptyComponent={
          !isLoading ? (
            <View style={styles.emptyContainer}>
              <Icon
                name={connectivityState !== 'online' ? 'cloud-offline-outline' : 'notifications-off-outline'}
                size={48}
                color={colors.borderMedium}
              />
              {connectivityState !== 'online' ? (
                <>
                  <Text style={styles.emptyTitle}>Notifications unavailable</Text>
                  <Text style={styles.emptySubtitle}>
                    {connectivityState === 'offline'
                      ? 'Notifications require an internet connection. Reconnect to load them.'
                      : 'The server is temporarily unavailable. Please try again in a moment.'}
                  </Text>
                </>
              ) : notificationsDisabled ? (
                <>
                  <Text style={styles.emptyTitle}>Notifications are disabled</Text>
                  <Text style={styles.emptySubtitle}>
                    Enable notifications in Settings &gt; Preferences &gt; Notifications to stay updated.
                  </Text>
                  <TouchableOpacity
                    style={styles.ctaButton}
                    onPress={() => router.push('/(tabs)/settings/notification-preferences' as any)}
                    accessibilityRole="button"
                    accessibilityLabel="Go to notification settings"
                    testID="notifications.go-to-settings"
                  >
                    <Text style={styles.ctaText}>Go to Settings</Text>
                  </TouchableOpacity>
                </>
              ) : (
                <>
                  <Text style={styles.emptyTitle}>No notifications</Text>
                  <Text style={styles.emptySubtitle}>
                    You're all caught up! We'll notify you about important updates here.
                  </Text>
                </>
              )}
            </View>
          ) : null
        }
      />
    </View>
  );
}
